import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { roomApi, roomSettingsApi, roomMembershipApi, Room, RoomBan, RoomApiKey, RoomMembershipOrder } from '../../utils/roomApi';
import { PaymentModal, PaymentType } from '../../modules/payment/components/PaymentModal';
import { getPaymentConfig } from '../../modules/payment/services/api';
import type { PaymentConfig } from '../../modules/payment/types';
import { Shield, Key, Users, Trash2, Plus, Copy, RefreshCw, Check, X, AlertTriangle, MessageSquare, QrCode, Bot, UserPlus, Wallet, Building2, AlertCircle, Calendar, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';

const RoomManagementPage: React.FC = () => {
  const { user, isAuthenticated, fetchUser } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'settings' | 'bans' | 'api' | 'ai' | 'members'>('settings');
  const [hasRoomPackage, setHasRoomPackage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [bans, setBans] = useState<RoomBan[]>([]);
  const [apiKeys, setApiKeys] = useState<RoomApiKey[]>([]);
  const [roomMembers, setRoomMembers] = useState<any[]>([]);
  const [membershipOrders, setMembershipOrders] = useState<RoomMembershipOrder[]>([]);

  const [showBanModal, setShowBanModal] = useState(false);
  const [banForm, setBanForm] = useState({ user_id: '', reason: '', duration_hours: '' });

  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [newApiKey, setNewApiKey] = useState<{ api_key: string; api_secret: string } | null>(null);

  const [showCreateRoomForm, setShowCreateRoomForm] = useState(false);
  const [createRoomForm, setCreateRoomForm] = useState({
    name: '',
    description: '',
    price: '',
    price_type: 'monthly',
    trc20_address: '',
    erc20_address: '',
    qr_code: '',
    max_members: '',
  });

  const [showManualEnableModal, setShowManualEnableModal] = useState(false);
  const [manualEnableForm, setManualEnableForm] = useState({
    user_id: '',
    username_or_email: '',
    membership_type: 'member',
    expires_at: '',
  });
  const [searchUsers, setSearchUsers] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState<PaymentType>('room_package');
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');

  const [showRoomCreationPaymentModal, setShowRoomCreationPaymentModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewPeriod, setRenewPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('token');
      if (!isAuthenticated && !token) {
        navigate('/login');
        return;
      }
      if (!user) {
        await fetchUser();
      }
      checkRoomPackage();
      fetchRooms();
      await fetchPaymentConfig();
    };
    init();
  }, []);

  const fetchPaymentConfig = async () => {
    try {
      const result = await getPaymentConfig();
      if (result.success) {
        setPaymentConfig(result.data);
      }
    } catch (error) {
      console.error('获取支付配置失败:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!isAuthenticated && !token) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const checkRoomPackage = () => {
    if (!user) return;
    const expiresAt = user.room_creator_expires_at;
    if (!expiresAt) {
      setHasRoomPackage(false);
      return;
    }
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    setHasRoomPackage(expirationDate > now);
  };

  const fetchRooms = async (search?: string) => {
    try {
      setLoading(true);
      const response = await roomApi.getRooms(user?.role === '站长', search);
      if (response.success && response.data) {
        let filteredRooms = response.data;
        if (user && user.role !== '站长') {
          filteredRooms = response.data.filter((room: Room) => room.owner_id === user.id);
        }
        setRooms(filteredRooms);

        const defaultRoomId = parseInt(searchParams.get('roomId') || '');
        const defaultRoom = filteredRooms.find((room: Room) => room.id === defaultRoomId);

        if (defaultRoom) {
          setSelectedRoom(defaultRoom);
        } else if (filteredRooms.length > 0) {
          setSelectedRoom(filteredRooms[0]);
        }
      }
    } catch (error) {
      console.error('获取房间列表失败:', error);
      toast.error('获取房间列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembershipOrders = async (roomId: number) => {
    try {
      const response = await roomMembershipApi.getOwnerRoomMembershipOrders(roomId);
      if (response.success) {
        setMembershipOrders(response.orders || []);
      }
    } catch (error) {
      console.error('获取会员订单失败:', error);
    }
  };

  const handleCreateRoom = () => {
    if (!createRoomForm.name.trim()) {
      toast.error('请输入房间名称');
      return;
    }

    setShowCreateRoomForm(false);
    setShowRoomCreationPaymentModal(true);
  };

  const handleRoomCreationSuccess = () => {
    toast.success('房间创建成功');
    setCreateRoomForm({
      name: '',
      description: '',
      price: '',
      price_type: 'monthly',
      trc20_address: '',
      erc20_address: '',
      qr_code: '',
      max_members: '',
    });
    fetchRooms();
  };

  useEffect(() => {
    if (selectedRoom) {
      fetchBans();
      fetchApiKeys();
    }
  }, [selectedRoom]);

  const fetchBans = async () => {
    if (!selectedRoom) return;
    try {
      const response = await roomSettingsApi.getBannedUsers(selectedRoom.id);
      if (response.success) {
        setBans(response.bans || []);
      }
    } catch (error) {
      console.error('获取禁言列表失败:', error);
    }
  };

  const fetchApiKeys = async () => {
    if (!selectedRoom) return;
    try {
      const response = await roomSettingsApi.getApiKeys(selectedRoom.id);
      if (response.success) {
        setApiKeys(response.keys || []);
      }
    } catch (error) {
      console.error('获取API密钥列表失败:', error);
    }
  };

  const handleBanUser = async () => {
    if (!selectedRoom || !banForm.user_id) {
      toast.error('请输入用户ID');
      return;
    }

    try {
      const response = await roomSettingsApi.banUser(selectedRoom.id, {
        user_id: parseInt(banForm.user_id),
        reason: banForm.reason || undefined,
        duration_hours: banForm.duration_hours ? parseInt(banForm.duration_hours) : undefined,
      });
      if (response.success) {
        toast.success('用户已被禁言');
        setShowBanModal(false);
        setBanForm({ user_id: '', reason: '', duration_hours: '' });
        fetchBans();
      } else {
        toast.error(response.message || '禁言失败');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '禁言失败');
    }
  };

  const handleUnbanUser = async (userId: number) => {
    if (!selectedRoom) return;
    if (!confirm('确定要解除该用户的禁言吗？')) return;

    try {
      const response = await roomSettingsApi.unbanUser(selectedRoom.id, userId);
      if (response.success) {
        toast.success('已解除禁言');
        fetchBans();
      } else {
        toast.error(response.message || '解除禁言失败');
      }
    } catch (error) {
      toast.error('解除禁言失败');
    }
  };

  const handleCreateApiKey = async () => {
    if (!selectedRoom) return;

    try {
      const response = await roomSettingsApi.createApiKey(selectedRoom.id, newApiKeyName || '默认密钥');
      if (response.success && response.data) {
        toast.success('API密钥创建成功');
        setNewApiKey({ api_key: response.data.api_key, api_secret: response.data.api_secret });
        setNewApiKeyName('');
        fetchApiKeys();
      } else {
        toast.error(response.message || '创建失败');
      }
    } catch (error) {
      toast.error('创建API密钥失败');
    }
  };

  const handleDeleteApiKey = async (keyId: number) => {
    if (!selectedRoom) return;
    if (!confirm('确定要删除这个API密钥吗？')) return;

    try {
      const response = await roomSettingsApi.deleteApiKey(selectedRoom.id, keyId);
      if (response.success) {
        toast.success('API密钥已删除');
        fetchApiKeys();
      } else {
        toast.error(response.message || '删除失败');
      }
    } catch (error) {
      toast.error('删除API密钥失败');
    }
  };

  const handleRegenerateSecret = async (keyId: number) => {
    if (!selectedRoom) return;
    if (!confirm('确定要重置API密钥吗？旧密钥将立即失效。')) return;

    try {
      const response = await roomSettingsApi.regenerateApiSecret(selectedRoom.id, keyId);
      if (response.success && response.data) {
        toast.success('API密钥已重置');
        setNewApiKey({ api_key: '', api_secret: response.data.api_secret });
        fetchApiKeys();
      } else {
        toast.error(response.message || '重置失败');
      }
    } catch (error) {
      toast.error('重置API密钥失败');
    }
  };

  const handleToggleApiKey = async (keyId: number) => {
    if (!selectedRoom) return;

    try {
      const response = await roomSettingsApi.toggleApiKeyStatus(selectedRoom.id, keyId);
      if (response.success) {
        fetchApiKeys();
      } else {
        toast.error(response.message || '修改失败');
      }
    } catch (error) {
      toast.error('修改API密钥状态失败');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('已复制到剪贴板');
  };

  const handleQrCodeUpload = async (e: React.ChangeEvent<HTMLInputElement>, isCreateForm: boolean = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('图片大小不能超过5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      const result = await response.json();

      if (result.success && result.data?.url) {
        if (isCreateForm) {
          setCreateRoomForm(prev => ({ ...prev, qr_code: result.data.url }));
        } else if (selectedRoom) {
          handleUpdateRoom({ qr_code: result.data.url });
        }
        toast.success('二维码上传成功');
      } else {
        toast.error(result.message || '上传失败');
      }
    } catch (error) {
      console.error('上传二维码失败:', error);
      toast.error('上传二维码失败');
    }

    e.target.value = '';
  };

  const handleUpdateRoom = async (data: Partial<Room>) => {
    if (!selectedRoom) return;

    try {
      const response = await roomApi.updateRoom(selectedRoom.id, data);
      if (response.success) {
        toast.success('房间设置已更新');
        fetchRooms();
      } else {
        toast.error(response.message || '更新失败');
      }
    } catch (error) {
      toast.error('更新房间设置失败');
    }
  };

  const getRoomExpiresAt = (room: Room) => {
    return room.expires_at || room.room_creator_expires_at;
  };

  const isRoomExpired = (room: Room) => {
    const expiresAt = getRoomExpiresAt(room);
    return expiresAt && new Date(expiresAt) < new Date();
  };

  const formatRoomExpiresAt = (room: Room) => {
    const expiresAt = getRoomExpiresAt(room);
    if (!expiresAt) return '永久';
    return new Date(expiresAt).toLocaleDateString('zh-CN');
  };

  const handleRoomRenew = () => {
    setShowRenewModal(true);
  };

  const handleConfirmRenew = async () => {
    if (!selectedRoom) return;
    try {
      setPaymentType('room_package');
      setSelectedPeriod(renewPeriod);
      setShowRenewModal(false);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('续费失败:', error);
      toast.error('续费失败');
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    if (selectedRoom) {
      try {
        await roomApi.renewRoom(selectedRoom.id, renewPeriod);
        toast.success('房间续费成功');
        fetchRooms();
      } catch (error) {
        console.error('续费失败:', error);
        toast.error('房间续费失败');
      }
    }
    fetchUser();
    setCreateRoomForm({
      name: '',
      description: '',
      price: '',
      price_type: 'monthly',
      trc20_address: '',
      erc20_address: '',
      qr_code: '',
      max_members: '',
    });
    setRoomCreationPaymentInfo(null);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
        </div>
      );
    }

    if (!isAuthenticated || !user) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">请先登录</h2>
          <p className="text-gray-600 mb-6">您需要登录后才能访问房间管理后台</p>
          <Link to="/login" className="btn btn-primary">
            去登录
          </Link>
        </div>
      );
    }

    const isAdminOrMaster = user && (user.role === '管理员' || user.role === '站长');

    if (!hasRoomPackage && user) {
      if (isAdminOrMaster) {
        return (
          <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="card p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-gray-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">管理员权限说明</h2>
              <p className="text-gray-600 mb-6">您是管理员/站长，无法开通房主功能。房主功能和管理员权限是分开的，互不兼容。</p>
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <p className="text-sm text-gray-700 mb-2"><strong>权限说明：</strong></p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 管理员只能使用后台管理功能</li>
                  <li>• 房主可以创建和管理自己的付费房间</li>
                  <li>• 同一用户不能同时拥有两种权限</li>
                </ul>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="card">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">开通房主功能</h2>
              <p className="text-gray-600">创建您的付费会员房间，开启盈利之路</p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">房间主人套餐</h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  创建多个付费会员房间
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  设置房间价格和收费模式
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  配置 TRC20 收款地址和二维码
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  管理房间成员和禁言
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  AI 助手功能配置
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="mb-4">
                <span className="text-gray-600">选择套餐周期</span>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('monthly')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      selectedPeriod === 'monthly'
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    月付<br />{paymentConfig?.roomPackage?.monthlyPrice || 15} USDT
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('quarterly')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      selectedPeriod === 'quarterly'
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    季付<br />{paymentConfig?.roomPackage?.quarterlyPrice || 35} USDT
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('yearly')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      selectedPeriod === 'yearly'
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    年费<br />{paymentConfig?.roomPackage?.yearlyPrice || 100} USDT
                  </button>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">房间主人套餐</span>
                  <span className="text-2xl font-bold text-purple-600">
                    {selectedPeriod === 'monthly' ? (paymentConfig?.roomPackage?.monthlyPrice || 15) :
                     selectedPeriod === 'quarterly' ? (paymentConfig?.roomPackage?.quarterlyPrice || 35) :
                     (paymentConfig?.roomPackage?.yearlyPrice || 100)} USDT
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {selectedPeriod === 'monthly' && '有效期1个月'}
                  {selectedPeriod === 'quarterly' && '有效期3个月'}
                  {selectedPeriod === 'yearly' && '有效期1年'}
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-800">支付说明</p>
                    <p className="text-yellow-700 mt-1">
                      1. 平台只收取房间开通费<br />
                      2. 用户付费进房的款项直接进入您的 TRC20 钱包，平台不参与分成<br />
                      3. 支付完成后系统自动开通权限
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  setPaymentType('room_package');
                  setShowPaymentModal(true);
                }}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-colors"
              >
                立即购买
              </button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const content = renderContent();
  
  if (content) {
    return (
      <>
        {content}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setHasRoomPackage(true);
            fetchRooms();
          }}
          paymentType={paymentType}
          selectedPeriod={selectedPeriod}
        />
      </>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">房间后台管理</h1>

      <div className="grid grid-cols-4 gap-6">
        <div className="col-span-1">
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">选择房间</h3>
            {user?.role === '站长' && (
              <div className="mb-3">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchRooms(searchTerm)}
                  placeholder="搜索房主..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => fetchRooms(searchTerm)}
                    className="flex-1 px-3 py-1.5 text-sm bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
                  >
                    搜索
                  </button>
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        fetchRooms();
                      }}
                      className="flex-1 px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      重置
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-2">
              {rooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    selectedRoom?.id === room.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="font-medium truncate">{room.name}</div>
                  <div className="text-xs text-gray-500">ID: {room.id}</div>
                </button>
              ))}
              {rooms.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-4">
                  {user.role === '站长' ? '暂无房间' : '您还没有创建房间'}
                </div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setSelectedRoom(null);
                  setShowCreateRoomForm(true);
                }}
                className="w-full flex items-center justify-center px-3 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                创建新房间
              </button>
            </div>
          </div>
        </div>

        <div className="col-span-3">
          {selectedRoom ? (
            <div className="card">
              <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-4">
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'settings'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Shield className="w-4 h-4 inline mr-1" />
                    房间设置
                  </button>
                  <button
                    onClick={() => setActiveTab('bans')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'bans'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Users className="w-4 h-4 inline mr-1" />
                    禁言管理
                  </button>
                  <button
                    onClick={() => setActiveTab('api')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'api'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Key className="w-4 h-4 inline mr-1" />
                    API密钥
                  </button>
                  <button
                    onClick={() => setActiveTab('ai')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'ai'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Bot className="w-4 h-4 inline mr-1" />
                    AI配置
                  </button>
                  <button
                    onClick={() => setActiveTab('members')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'members'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <UserPlus className="w-4 h-4 inline mr-1" />
                    成员管理
                  </button>
                </nav>
              </div>

              {activeTab === 'settings' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">房间名称</label>
                      <input
                        type="text"
                        defaultValue={selectedRoom.name}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onBlur={(e) => handleUpdateRoom({ name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">价格 (USDT)</label>
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={selectedRoom.price}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onBlur={(e) => handleUpdateRoom({ price: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">收费模式</label>
                      <select
                        defaultValue={selectedRoom.price_type}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onChange={(e) => handleUpdateRoom({ price_type: e.target.value as any })}
                      >
                        <option value="monthly">月付</option>
                        <option value="yearly">年付</option>
                        <option value="one_time">一次性</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">房间描述</label>
                    <textarea
                      defaultValue={selectedRoom.description}
                      rows={2}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      onBlur={(e) => handleUpdateRoom({ description: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        <QrCode className="w-3 h-3 inline mr-1" />
                        TRC20 收款地址
                      </label>
                      <input
                        type="text"
                        defaultValue={selectedRoom.trc20_address || ''}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onBlur={(e) => handleUpdateRoom({ trc20_address: e.target.value })}
                        placeholder="TRC20 钱包地址"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        <QrCode className="w-3 h-3 inline mr-1" />
                        ERC20 收款地址
                      </label>
                      <input
                        type="text"
                        defaultValue={selectedRoom.erc20_address || ''}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onBlur={(e) => handleUpdateRoom({ erc20_address: e.target.value })}
                        placeholder="ERC20 钱包地址"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">最大成员数</label>
                    <input
                      type="number"
                      defaultValue={selectedRoom.max_members || ''}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      onBlur={(e) => handleUpdateRoom({ max_members: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="不限制"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <QrCode className="w-3 h-3 inline mr-1" />
                      收款二维码
                    </label>
                    <div className="mt-1 flex items-center">
                      <label className="btn btn-outline btn-xs cursor-pointer inline-flex items-center">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleQrCodeUpload(e, false)}
                        />
                        <Plus className="w-3 h-3 mr-1" />
                        上传
                      </label>
                      <span className="ml-2 text-xs text-gray-500">JPG/PNG，最大 5MB</span>
                    </div>
                    {selectedRoom.qr_code && (
                      <div className="mt-1.5 flex items-center">
                        <img src={selectedRoom.qr_code} alt="QR Code" className="w-16 h-16 object-contain border rounded" />
                        <button
                          onClick={() => handleUpdateRoom({ qr_code: '' })}
                          className="ml-2 text-red-500 hover:text-red-700 text-xs"
                        >
                          删除
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-1.5">
                      <input
                        type="checkbox"
                        defaultChecked={selectedRoom.is_active === 1}
                        onChange={(e) => handleUpdateRoom({ is_active: e.target.checked ? 1 : 0 })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-xs text-gray-700">启用房间</span>
                    </label>
                    <label className="flex items-center space-x-1.5">
                      <input
                        type="checkbox"
                        defaultChecked={selectedRoom.is_hidden === 1}
                        onChange={(e) => handleUpdateRoom({ is_hidden: e.target.checked ? 1 : 0 })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-xs text-gray-700">隐藏房间</span>
                    </label>
                  </div>

                  <div className="mt-4 border-t border-gray-200 pt-3">
                    <h4 className="text-xs font-semibold text-gray-900 mb-2">房间有效期</h4>
                    <div className={`p-3 rounded-lg ${isRoomExpired(selectedRoom) ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-900">
                            到期时间: <span className={isRoomExpired(selectedRoom) ? 'text-red-600 font-bold' : 'text-gray-700'}>
                              {formatRoomExpiresAt(selectedRoom)}
                            </span>
                          </p>
                          {isRoomExpired(selectedRoom) && (
                            <p className="text-xs text-red-600 mt-0.5">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              房间已过期
                            </p>
                          )}
                        </div>
                        <button
                          onClick={handleRoomRenew}
                          className="inline-flex items-center px-2.5 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
                        >
                          <CreditCard className="w-3 h-3 mr-1" />
                          续费
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'bans' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">禁言列表</h3>
                    <button
                      onClick={() => setShowBanModal(true)}
                      className="btn btn-primary btn-sm flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      添加禁言
                    </button>
                  </div>

                  {bans.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>暂无禁言用户</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {bans.map((ban) => (
                        <div
                          key={ban.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <div className="font-medium text-gray-900">
                              {ban.nickname || ban.username || `用户 ${ban.user_id}`}
                            </div>
                            <div className="text-sm text-gray-500">
                              {ban.reason && <span>原因: {ban.reason}</span>}
                              {ban.expires_at && (
                                <span className="ml-2">
                                  过期: {new Date(ban.expires_at).toLocaleString()}
                                </span>
                              )}
                              {!ban.expires_at && <span className="text-red-500">永久禁言</span>}
                            </div>
                          </div>
                          <button
                            onClick={() => handleUnbanUser(ban.user_id)}
                            className="btn btn-sm btn-secondary"
                          >
                            解除禁言
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'ai' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">AI 功能配置</h3>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <div className="flex items-center mb-2">
                      <Bot className="w-5 h-5 text-blue-600 mr-2" />
                      <span className="font-medium text-blue-800">AI 助手功能</span>
                    </div>
                    <p className="text-sm text-blue-700">
                      配置房间内的 AI 助手功能，为会员提供智能问答服务。
                      AI 功能可以帮助会员解答问题，提高房间活跃度。
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">启用 AI 助手</div>
                        <div className="text-sm text-gray-500">开启后，会员可以在房间内使用 AI 问答功能</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">AI 响应频率限制</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="low">低（每用户每小时10次）</option>
                        <option value="medium">中（每用户每小时30次）</option>
                        <option value="high">高（每用户每小时60次）</option>
                        <option value="unlimited">不限制</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">AI 模型选择</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="gpt-3.5">GPT-3.5（快速响应）</option>
                        <option value="gpt-4">GPT-4（更高质量）</option>
                        <option value="claude">Claude（平衡型）</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">系统提示词</label>
                      <textarea
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="定义 AI 助手的角色和行为规范..."
                        defaultValue="你是一个专业的外汇交易助手，可以回答会员关于 EA 编程、交易策略、市场分析等问题。请用专业、友好的态度回复。"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'members' && (
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">成员管理</h3>
                    <button
                      onClick={() => setShowManualEnableModal(true)}
                      className="btn btn-primary btn-sm flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      手动开通会员
                    </button>
                  </div>

                  <div className="mb-4 border-b border-gray-200">
                    <div className="flex space-x-4">
                      <button
                        onClick={() => {
                          if (selectedRoom) {
                            roomApi.getRoomMembers(selectedRoom.id).then(res => {
                              if (res.success) setRoomMembers(res.members || []);
                            });
                          }
                        }}
                        className={`pb-2 px-1 font-medium text-sm border-b-2 ${
                          true
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        当前成员
                      </button>
                      <button
                        onClick={() => {
                          if (selectedRoom) {
                            fetchMembershipOrders(selectedRoom.id);
                          }
                        }}
                        className={`pb-2 px-1 font-medium text-sm border-b-2 ${
                          false
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        已开通会员
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-gray-900">当前成员列表</h4>
                    <div className="flex items-center">
                      <input
                        type="text"
                        placeholder="搜索成员..."
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {roomMembers.length > 0 ? (
                    <div className="space-y-2">
                      {roomMembers.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium">
                              {member.nickname?.charAt(0) || '?'}
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">{member.nickname}</div>
                              <div className="text-sm text-gray-500">
                                {member.role === 'owner' ? '房主' : member.role === 'admin' ? '管理员' : '成员'}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              if (selectedRoom) {
                                roomApi.removeMember(selectedRoom.id, member.id).then(res => {
                                  if (res.success) {
                                    toast.success('移除成功');
                                    roomApi.getRoomMembers(selectedRoom.id).then(r => {
                                      if (r.success) setRoomMembers(r.members || []);
                                    });
                                  }
                                });
                              }
                            }}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      暂无成员
                    </div>
                  )}

                  <div className="mt-8">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-gray-900">已开通会员列表</h4>
                      <button
                        onClick={() => selectedRoom && fetchMembershipOrders(selectedRoom.id)}
                        className="inline-flex items-center px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        刷新
                      </button>
                    </div>

                    {membershipOrders.length > 0 ? (
                      <div className="space-y-2">
                        {membershipOrders.map((order) => (
                          <div
                            key={order.id}
                            className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                          >
                            <div className="flex items-center">
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium">
                                {order.username?.charAt(0) || '?'}
                              </div>
                              <div className="ml-3">
                                <div className="font-medium text-gray-900">{order.username}</div>
                                <div className="text-sm text-gray-500">
                                  订单号: {order.id} | 金额: {order.amount} USDT |{' '}
                                  {order.price_type === 'monthly' ? '月付' : order.price_type === 'yearly' ? '年付' : '一次性'}
                                </div>
                                <div className="text-xs text-gray-400">
                                  开通时间: {new Date(order.created_at).toLocaleString('zh-CN')}
                                  {order.expired_at && ` | 到期时间: ${new Date(order.expired_at).toLocaleString('zh-CN')}`}
                                </div>
                              </div>
                            </div>
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                              已开通
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        暂无已开通会员记录
                      </div>
                    )}
                  </div>

                  <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center mb-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                      <span className="font-medium text-yellow-800">注意事项</span>
                    </div>
                    <p className="text-sm text-yellow-700">
                      手动开通功能用于特殊情况，例如：用户通过链上转账但未自动到账、
                      优惠活动、赠送会员等。开通记录将被日志记录。
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'api' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">API密钥管理</h3>
                    <button
                      onClick={() => {
                        setNewApiKey(null);
                        setShowApiKeyModal(true);
                      }}
                      className="btn btn-primary btn-sm flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      创建密钥
                    </button>
                  </div>

                  {newApiKey && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-green-800">新创建的API密钥</span>
                        <button
                          onClick={() => setNewApiKey(null)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <span className="w-20 text-gray-600">API Key:</span>
                          <code className="flex-1 bg-white px-2 py-1 rounded break-all">{newApiKey.api_key}</code>
                          <button
                            onClick={() => copyToClipboard(newApiKey.api_key)}
                            className="ml-2 text-gray-500 hover:text-gray-700"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center">
                          <span className="w-20 text-gray-600">API Secret:</span>
                          <code className="flex-1 bg-white px-2 py-1 rounded break-all">{newApiKey.api_secret}</code>
                          <button
                            onClick={() => copyToClipboard(newApiKey.api_secret)}
                            className="ml-2 text-gray-500 hover:text-gray-700"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-red-600">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        请妥善保存密钥，关闭后将无法再次查看完整密钥
                      </p>
                    </div>
                  )}

                  {apiKeys.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Key className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>暂无API密钥</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {apiKeys.map((key) => (
                        <div
                          key={key.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              <span className="font-medium text-gray-900">{key.name}</span>
                              <span
                                className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                                  key.is_active
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                {key.is_active ? '启用' : '禁用'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              <span className="font-mono">{key.api_key}</span>
                              <span className="ml-4">
                                最后使用: {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : '从未使用'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleToggleApiKey(key.id)}
                              className={`p-2 rounded-lg ${
                                key.is_active
                                  ? 'text-red-600 hover:bg-red-50'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={key.is_active ? '禁用' : '启用'}
                            >
                              {key.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleRegenerateSecret(key.id)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                              title="重置密钥"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteApiKey(key.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="删除"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">外部API调用说明</h4>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p><strong>请求地址:</strong> <code>POST /api/external/room/send-message</code></p>
                      <p><strong>Headers:</strong></p>
                      <ul className="ml-4 list-disc">
                        <li><code>X-API-Key</code>: 您的API密钥</li>
                        <li><code>X-API-Secret</code>: 您的API密钥</li>
                      </ul>
                      <p><strong>Body (JSON):</strong></p>
                      <ul className="ml-4 list-disc">
                        <li><code>room_id</code>: 房间ID</li>
                        <li><code>content</code>: 消息内容</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card">
              {!showCreateRoomForm ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-purple-400" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">欢迎使用房间管理后台</h3>
                  <p className="text-gray-500 mb-6">
                    {user.role === '站长' ? '暂无房间，请先创建房间' : '您还没有创建房间'}
                  </p>
                  <button
                    onClick={() => setShowCreateRoomForm(true)}
                    className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    去创建房间
                  </button>
                </div>
              ) : (
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6">创建新房间</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">房间名称 *</label>
                      <input
                        type="text"
                        value={createRoomForm.name}
                        onChange={(e) => setCreateRoomForm({ ...createRoomForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="输入房间名称"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">房间描述</label>
                      <textarea
                        value={createRoomForm.description}
                        onChange={(e) => setCreateRoomForm({ ...createRoomForm, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="输入房间描述"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">价格 (USDT)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={createRoomForm.price}
                          onChange={(e) => setCreateRoomForm({ ...createRoomForm, price: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">收费模式</label>
                        <select
                          value={createRoomForm.price_type}
                          onChange={(e) => setCreateRoomForm({ ...createRoomForm, price_type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="monthly">月付</option>
                          <option value="yearly">年付</option>
                          <option value="one_time">一次性</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <QrCode className="w-4 h-4 inline mr-1" />
                          TRC20 收款地址
                        </label>
                        <input
                          type="text"
                          value={createRoomForm.trc20_address}
                          onChange={(e) => setCreateRoomForm({ ...createRoomForm, trc20_address: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="输入您的 TRC20 钱包地址"
                        />
                        <p className="text-xs text-gray-500 mt-1">TRC20 (TRON 网络)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <QrCode className="w-4 h-4 inline mr-1" />
                          ERC20 收款地址
                        </label>
                        <input
                          type="text"
                          value={createRoomForm.erc20_address}
                          onChange={(e) => setCreateRoomForm({ ...createRoomForm, erc20_address: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="输入您的 ERC20 钱包地址"
                        />
                        <p className="text-xs text-gray-500 mt-1">ERC20 (Ethereum 网络)</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Users className="w-4 h-4 inline mr-1" />
                        最大成员数
                      </label>
                      <input
                        type="number"
                        value={createRoomForm.max_members}
                        onChange={(e) => setCreateRoomForm({ ...createRoomForm, max_members: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="留空表示不限制"
                      />
                      <p className="text-xs text-gray-500 mt-1">房间最大成员数量限制</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <QrCode className="w-4 h-4 inline mr-1" />
                        收款二维码
                      </label>
                      <div className="mt-1">
                        <label className="btn btn-outline btn-sm cursor-pointer inline-flex items-center">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleQrCodeUpload(e, true)}
                          />
                          <Plus className="w-4 h-4 mr-1" />
                          上传二维码图片
                        </label>
                        <span className="ml-2 text-xs text-gray-500">支持 JPG/PNG，最大 5MB</span>
                      </div>
                      {createRoomForm.qr_code && (
                        <div className="mt-2 flex items-center">
                          <img src={createRoomForm.qr_code} alt="QR Code" className="w-24 h-24 object-contain border rounded" />
                          <button
                            onClick={() => setCreateRoomForm(prev => ({ ...prev, qr_code: '' }))}
                            className="ml-2 text-red-500 hover:text-red-700 text-sm"
                          >
                            删除
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">可选：提供二维码图片方便用户扫码支付</p>
                    </div>
                  </div>
                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowCreateRoomForm(false);
                        setCreateRoomForm({
                          name: '',
                          description: '',
                          price: '',
                          price_type: 'monthly',
                          trc20_address: '',
                          qr_code: '',
                          max_members: '',
                        });
                      }}
                      className="flex-1 btn btn-secondary"
                    >
                      取消
                    </button>
                    <button onClick={handleCreateRoom} className="flex-1 btn btn-primary">
                      创建房间
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showBanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">添加禁言</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户ID *</label>
                <input
                  type="number"
                  value={banForm.user_id}
                  onChange={(e) => setBanForm({ ...banForm, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="输入用户ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">禁言原因</label>
                <input
                  type="text"
                  value={banForm.reason}
                  onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="输入禁言原因"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">禁言时长（小时）</label>
                <input
                  type="number"
                  value={banForm.duration_hours}
                  onChange={(e) => setBanForm({ ...banForm, duration_hours: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="留空表示永久禁言"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowBanModal(false);
                  setBanForm({ user_id: '', reason: '', duration_hours: '' });
                }}
                className="flex-1 btn btn-secondary"
              >
                取消
              </button>
              <button onClick={handleBanUser} className="flex-1 btn btn-primary">
                禁言
              </button>
            </div>
          </div>
        </div>
      )}

      {showApiKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">创建API密钥</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">密钥名称</label>
              <input
                type="text"
                value={newApiKeyName}
                onChange={(e) => setNewApiKeyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="输入密钥名称（可选）"
              />
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowApiKeyModal(false);
                  setNewApiKeyName('');
                }}
                className="flex-1 btn btn-secondary"
              >
                取消
              </button>
              <button onClick={handleCreateApiKey} className="flex-1 btn btn-primary">
                创建
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualEnableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">手动开通会员</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户名/邮箱 *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={manualEnableForm.username_or_email}
                    onChange={(e) => {
                      setManualEnableForm({ ...manualEnableForm, username_or_email: e.target.value, user_id: '' });
                    }}
                    onKeyUp={async (e) => {
                      if (e.target.value.length >= 2) {
                        setSearching(true);
                        try {
                          const response = await roomApi.searchUsers(e.target.value);
                          if (response.success) {
                            setSearchUsers(response.users || []);
                          }
                        } catch (error) {
                          console.error('搜索用户失败:', error);
                        }
                        setSearching(false);
                      } else {
                        setSearchUsers([]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="输入用户名或邮箱搜索用户"
                  />
                  {searching && (
                    <RefreshCw className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                  )}
                </div>
                {searchUsers.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                    {searchUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => {
                          setManualEnableForm({
                            ...manualEnableForm,
                            user_id: user.id.toString(),
                            username_or_email: user.username || user.email,
                          });
                          setSearchUsers([]);
                        }}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                      >
                        <div>
                          <div className="font-medium text-gray-900">{user.username}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                        <Check className={`w-5 h-5 ${manualEnableForm.user_id === user.id.toString() ? 'text-green-500' : 'text-gray-300'}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">会员类型</label>
                <select
                  value={manualEnableForm.membership_type}
                  onChange={(e) => setManualEnableForm({ ...manualEnableForm, membership_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="member">普通会员</option>
                  <option value="admin">房间管理员</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">过期时间</label>
                <input
                  type="datetime-local"
                  value={manualEnableForm.expires_at}
                  onChange={(e) => setManualEnableForm({ ...manualEnableForm, expires_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">留空表示永久会员</p>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowManualEnableModal(false);
                  setManualEnableForm({ user_id: '', username_or_email: '', membership_type: 'member', expires_at: '' });
                  setSearchUsers([]);
                }}
                className="flex-1 btn btn-secondary"
              >
                取消
              </button>
              <button
                onClick={async () => {
                  if (!selectedRoom || !manualEnableForm.user_id) {
                    toast.error('请选择用户');
                    return;
                  }
                  try {
                    const response = await roomApi.manualEnableMember(
                      selectedRoom.id,
                      parseInt(manualEnableForm.user_id),
                      manualEnableForm.membership_type,
                      manualEnableForm.expires_at
                    );
                    if (response.success) {
                      toast.success('会员开通成功');
                      setShowManualEnableModal(false);
                      setManualEnableForm({ user_id: '', username_or_email: '', membership_type: 'member', expires_at: '' });
                      setSearchUsers([]);
                      // 刷新会员订单列表
                      if (selectedRoom) {
                        fetchMembershipOrders(selectedRoom.id);
                      }
                    } else {
                      toast.error(response.message || '开通失败');
                    }
                  } catch (error: any) {
                    toast.error(error.response?.data?.message || '开通失败');
                  }
                }}
                className="flex-1 btn btn-primary"
              >
                确认开通
              </button>
            </div>
          </div>
        </div>
      )}

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        paymentType={paymentType}
        selectedPeriod={selectedPeriod}
      />

      <PaymentModal
        isOpen={showRoomCreationPaymentModal}
        onClose={() => setShowRoomCreationPaymentModal(false)}
        onSuccess={handleRoomCreationSuccess}
        paymentType="room_creation"
        roomName={createRoomForm.name}
        roomDescription={createRoomForm.description}
      />

      {showRenewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">续费房间</h3>
              <button
                onClick={() => setShowRenewModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">选择续费时长：</p>
              <div className="space-y-3">
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="renew-period"
                    value="monthly"
                    checked={renewPeriod === 'monthly'}
                    onChange={(e) => setRenewPeriod(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                    className="mr-3"
                  />
                  <span className="font-medium text-gray-800">月付</span>
                  <span className="ml-auto text-blue-600 font-semibold">
                    {paymentConfig?.roomPackage?.monthlyPrice || 0} USDT
                  </span>
                </label>
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="renew-period"
                    value="quarterly"
                    checked={renewPeriod === 'quarterly'}
                    onChange={(e) => setRenewPeriod(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                    className="mr-3"
                  />
                  <span className="font-medium text-gray-800">季付</span>
                  <span className="ml-auto text-blue-600 font-semibold">
                    {paymentConfig?.roomPackage?.quarterlyPrice || 0} USDT
                  </span>
                </label>
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="renew-period"
                    value="yearly"
                    checked={renewPeriod === 'yearly'}
                    onChange={(e) => setRenewPeriod(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                    className="mr-3"
                  />
                  <span className="font-medium text-gray-800">年付</span>
                  <span className="ml-auto text-blue-600 font-semibold">
                    {paymentConfig?.roomPackage?.yearlyPrice || 0} USDT
                  </span>
                </label>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowRenewModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirmRenew}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
                >
                  确认续费
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomManagementPage;